/**
 * The review queue's Confirm, second step (COL-350/508): what one-click
 * Confirm would book for a queue row, why it can't, and the dialog that
 * restates tenant, unit and rent month before anything is booked. Shared by
 * the reconciliation review page and colivIQ Brain's reconcile card
 * (COL-527), so the two can never show different details for one Confirm.
 */
import type { ReactNode } from "react";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatCents } from "@/lib/money";
import { isOpenRentStatus } from "@/lib/rent";
import {
  feePortionError,
  periodLabel,
  planTransferSplit,
  splitSummary,
  type FeePortionError,
  type TransferSplit,
} from "@/lib/transfer-split";
// Types only: the hooks module builds the Supabase client at import time.
import type {
  OpenInvoice,
  ReconciliationMatchRow,
} from "@/hooks/v2/useReconciliation";

type Translate = (key: string, fallback?: string) => string;

/** The part of the transfer a queue row is about (COL-508): a remainder row
 *  carries its own amount; every other row is the whole transfer. */
export function availableCents(
  row: ReconciliationMatchRow | null | undefined
): number {
  if (!row) return 0;
  return row.amount_cents ?? row.bank_transactions?.amount_cents ?? 0;
}

/** Whether a row accounts for only part of its transfer. */
export function isTransferPortion(row: ReconciliationMatchRow): boolean {
  const total = row.bank_transactions?.amount_cents;
  return (
    row.amount_cents != null && total != null && row.amount_cents !== total
  );
}

/** The tenancy's Mahngebühren pot, in cents. */
export function feesOwedCents(
  p: { bookings?: { accumulated_fees_eur?: number | null } | null } | null
): number {
  return Math.max(
    0,
    Math.round((Number(p?.bookings?.accumulated_fees_eur) || 0) * 100)
  );
}

/** A suggestion is only actionable while its candidate invoice is still
 * open. A payment can get settled while the row waits in the queue (another
 * transfer's confirm, SEPA collection, a manual mark-paid) — confirming
 * against it would no-op at the paid-guard and just confuse the admin, so
 * the row is flagged "Already paid" and Confirm is disabled instead. */
export function candidateStillOpen(
  p: ReconciliationMatchRow["payments"]
): boolean {
  if (!p) return false;
  // COL-417: `partially_paid` belongs here. It is an allow-list, so without it
  // a part-paid invoice reads as SETTLED — the queue would flag "already paid"
  // and disable Confirm on exactly the invoice a second transfer is meant to
  // close, which is the whole case this feature exists for.
  return isOpenRentStatus(p.status);
}

export function feeErrorMessage(
  error: FeePortionError,
  owedCents: number,
  currency: string,
  t: Translate
): string {
  return error === "exceeds_transfer"
    ? t(
        "admin.reconciliation.split.feeExceedsTransfer",
        "The fee portion can't be more than the transfer."
      )
    : t(
        "admin.reconciliation.split.feeExceedsOwed",
        "Only {amount} of Mahngebühren are owed."
      ).replace("{amount}", formatCents(owedCents, currency));
}

/** "Confirm books: …" — what one-click Confirm will book, shared by the row
 * hint and the confirmation dialog so the two can never disagree. */
export function confirmBooksText(
  split: TransferSplit,
  currency: string,
  t: Translate,
  language: string
): string {
  return t(
    "admin.reconciliation.split.confirmHint",
    "Confirm books: {summary}"
  ).replace(
    "{summary}",
    [
      splitSummary(split, currency, t, language),
      // The suggested month itself may be the one a SEPA/card debit is
      // already collecting — say why it gets nothing.
      ...split.skipped_in_flight.map((m) =>
        t(
          "admin.reconciliation.split.partSkipped",
          "{month} skipped (SEPA/card collection in progress)"
        ).replace("{month}", periodLabel(m.due_date, language))
      ),
    ].join(" · ")
  );
}

/** Whether a split is just "all of it onto the suggested invoice". */
export function isSimpleSplit(
  split: TransferSplit,
  suggestedPaymentId: string | null
): boolean {
  return (
    split.rent.length === 1 &&
    split.rent[0].payment_id === suggestedPaymentId &&
    split.fee_cents === 0 &&
    split.credit_cents === 0 &&
    split.remainder_cents === 0 &&
    split.skipped_in_flight.length === 0
  );
}

/** One labelled line of the confirmation dialog. */
function ConfirmDetail({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <dt className="shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words text-right font-medium">{children}</dd>
    </div>
  );
}

/** Second confirmation before Confirm books a transfer: restates which money
 * goes to which tenant and month, so a mis-click on a dense queue row is
 * caught before it is booked. */
export function ConfirmAssignmentDialog({
  row,
  split,
  blocked,
  blockedReason,
  pending,
  onCancel,
  onConfirm,
}: {
  row: ReconciliationMatchRow | null;
  /** What Confirm would book; null when the suggested invoice is no longer open. */
  split: TransferSplit | null;
  blocked: boolean;
  /** Why Assign is disabled, when it is more than "still loading". */
  blockedReason: string | null;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t, language } = useLanguage();
  const tx = row?.bank_transactions;
  const p = row?.payments;
  const currency = tx?.currency ?? p?.currency ?? "EUR";
  const room = p?.bookings?.rooms;
  const where = [room?.properties?.title, room?.title]
    .filter(Boolean)
    .join(" · ");
  return (
    <Dialog
      open={!!row}
      onOpenChange={(open) => {
        if (!open && !pending) onCancel();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-petrol/10 text-petrol">
              <Check className="h-5 w-5" />
            </span>
            {t(
              "admin.reconciliation.confirmDialog.title",
              "Confirm assignment"
            )}
          </DialogTitle>
          <DialogDescription>
            {t(
              "admin.reconciliation.confirmDialog.description",
              "Check the details below. Confirming books this transfer against the tenant's rent."
            )}
          </DialogDescription>
        </DialogHeader>
        {row && (
          <dl
            className="space-y-2 rounded-md border bg-muted/30 p-3 text-sm"
            data-testid="confirm-dialog-details"
          >
            <ConfirmDetail
              label={t(
                "admin.reconciliation.confirmDialog.transfer",
                "Transfer"
              )}
            >
              {formatCents(availableCents(row), currency)}
              {isTransferPortion(row) && tx && (
                <div className="text-xs font-normal text-muted-foreground">
                  {t(
                    "admin.reconciliation.split.leftOfTransfer",
                    "left from a transfer of {total}"
                  ).replace("{total}", formatCents(tx.amount_cents, currency))}
                </div>
              )}
            </ConfirmDetail>
            <ConfirmDetail
              label={t("admin.reconciliation.confirmDialog.from", "From")}
            >
              {tx?.counterpart_name ||
                t("admin.reconciliation.unknownSender", "Unknown sender")}
            </ConfirmDetail>
            <ConfirmDetail
              label={t(
                "admin.reconciliation.confirmDialog.purpose",
                "Payment purpose"
              )}
            >
              {tx?.purpose || "—"}
            </ConfirmDetail>
            <ConfirmDetail
              label={t("admin.reconciliation.confirmDialog.tenant", "Tenant")}
            >
              {row.tenantName ||
                row.tenantEmail ||
                t("admin.reconciliation.unknownTenant", "Unknown tenant")}
              {where && (
                <div className="text-xs font-normal text-muted-foreground">
                  {where}
                </div>
              )}
            </ConfirmDetail>
            <ConfirmDetail
              label={t("admin.reconciliation.confirmDialog.rent", "Rent")}
            >
              {p
                ? `${periodLabel(p.due_date, language)} · ${formatCents(p.amount_cents, p.currency)}`
                : "—"}
            </ConfirmDetail>
          </dl>
        )}
        {/* More than "all of it onto this invoice": say what is booked. */}
        {row && split && !isSimpleSplit(split, row.payment_id) && (
          <p
            className="text-xs text-blue-700"
            data-testid="confirm-dialog-split"
          >
            {confirmBooksText(split, currency, t, language)}
          </p>
        )}
        {row && blockedReason && (
          <p className="text-xs text-rose-600" role="alert">
            {blockedReason}
          </p>
        )}
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onCancel} disabled={pending}>
            {t("admin.reconciliation.cancel", "Cancel")}
          </Button>
          <Button onClick={onConfirm} disabled={blocked || pending}>
            {t("admin.reconciliation.confirmDialog.submit", "Assign transfer")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** What one-click Confirm would book for a queue row (credit on), or null
 * when its suggested invoice is gone or no longer open. `feeOverride` is the
 * admin-stated Mahngebühr portion; null lets the split rule decide. */
export function planConfirm(
  row: ReconciliationMatchRow,
  openInvoices: readonly OpenInvoice[],
  feeOverride: number | null
): {
  owed: number;
  feeOverride: number | null;
  error: FeePortionError | null;
  split: TransferSplit;
} | null {
  const p = row.payments;
  if (!p || !candidateStillOpen(p)) return null;
  const owed = feesOwedCents(p);
  const error = feePortionError(feeOverride, availableCents(row), owed);
  return {
    owed,
    feeOverride,
    error,
    split: planTransferSplit({
      availableCents: availableCents(row),
      anchor: p,
      rows: openInvoices,
      feesOwedCents: owed,
      feeOverrideCents: error ? null : feeOverride,
      creditNext: true,
    }),
  };
}

/** Why Confirm can't book this row right now, or null when it can. */
export function describeConfirmBlock(
  row: ReconciliationMatchRow,
  plan: ReturnType<typeof planConfirm>,
  invoicesFailed: boolean,
  t: Translate
): string | null {
  if (!plan)
    return t(
      "admin.reconciliation.suggestionSettledTitle",
      "The suggested invoice is no longer open — it was settled after this suggestion was made"
    );
  if (invoicesFailed)
    return t(
      "admin.reconciliation.split.tenancyLoadFailed",
      "Couldn't load this tenancy's open months, so what Confirm would book can't be shown. Retry before confirming."
    );
  if (plan.error)
    return feeErrorMessage(
      plan.error,
      plan.owed,
      row.bank_transactions?.currency ?? "EUR",
      t
    );
  if (plan.split.applied_cents === 0)
    return t(
      "admin.reconciliation.split.nothingApplicable",
      "Nothing on this tenancy can take this transfer — every open month is already being collected by SEPA/card. Assign it to another invoice or reject it."
    );
  return null;
}
